import React, { useState, useEffect } from "react";
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { SearchBar, GeneralMessage } from "../../../../common/APICommon";
import { ConfirmationModal, DisapproveReasonModal } from "../../../../common/Modal";
import { useTransactionRequest } from "../../../hooks/useTransactionRequest";
import { TransactionRequestTable } from "./components/TransactionRequestTable";
import { RequestDetailsModal } from "./components/RequestDetailsModal";
import { LoadingSpinner } from "../../../../common/Loading";
import { FEATURES } from "../../../../config/features";

const TABS_CONFIG = [
  { label: "ALL", type: 0 },
  { label: "OVERTIME", type: 3 },
  { label: "DTR CORRECTION", type: 4 },
  { label: "LEAVE", type: 1 },
  { label: "CHANGE SCHEDULE", type: 2 },
  { label: "OFFSET", type: 5 },
  { label: "LEAVE RECALL", type: 6 },
];

const DEFAULT_CONFIRMATION = { title: "Confirm Approval", actionText: "approve" };

export function TransactionRequestModule({ user }) {
  const {
    loading, data, searchTerm, setSearchTerm, fetchData, handleAction,
    showMessage, message, isSuccess
  } = useTransactionRequest(user);

  const [selectedTab, setSelectedTab] = useState(0);
  const [targetRequest, setTargetRequest] = useState(null);

  const [confirmationConfig, setConfirmationConfig] = useState(DEFAULT_CONFIRMATION);

  // Modal States
  const [showDetails, setShowDetails] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showDisapproveModal, setShowDisapproveModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  function handleCloseConfirm() {
    setConfirmationConfig(DEFAULT_CONFIRMATION);
    setShowApproveConfirm(false);
  }

  useEffect(() => {
    fetchData(TABS_CONFIG[selectedTab].type);
    setCurrentPage(1);
  }, [selectedTab, fetchData]);

  const onProcessAction = async (status, remarks = "") => {
    const result = await handleAction(targetRequest, status, remarks);
    if (result.success) {
      setShowApproveConfirm(false);
      setShowDisapproveModal(false);
      fetchData(TABS_CONFIG[selectedTab].type); // Refresh
    }
  };

  const tabStyle = (index) => `px-4 py-2 cursor-pointer font-medium transition-colors outline-none select-none ${selectedTab === index ? "bg-[#668fca] text-white rounded-t-lg" : "text-[#668fca] hover:text-[#4a6ea5]"}`;

  return (
    <div className="mainData p-5">
      <div className="flex justify-between items-center mb-6">
        <SearchBar value={searchTerm} handleFilter={setSearchTerm} placeholder="Search Employee or Ref #..." />
      </div>

      <Tabs selectedIndex={selectedTab} onSelect={setSelectedTab}>
        <TabList className="flex flex-row items-center border-b border-gray-200 mb-4 p-0 list-none">
          {TABS_CONFIG.map((tab, idx) => (
            <Tab key={idx} className={tabStyle(idx)}><b>{tab.label}</b></Tab>
          ))}
        </TabList>

        {TABS_CONFIG.map((tab, idx) => (
          <TabPanel key={idx}>
            <TransactionRequestTable
              data={data}
              requestType={tab.type}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              setConfirmationConfig={setConfirmationConfig}
              onView={(item) => { setTargetRequest(item); setShowDetails(true); }}
              onApprove={(item) => { setTargetRequest(item); setShowApproveConfirm(true); }}
              onDisapprove={(item) => { setTargetRequest(item); setShowDisapproveModal(true); }}
            />
          </TabPanel>
        ))}
      </Tabs>

      {/* Details Modal (Read Only) */}
      <RequestDetailsModal
        isOpen={showDetails}
        onClose={() => setShowDetails(false)}
        request={targetRequest}
      />

      {/* Approval Confirmation (Uniform Design) */}
      <ConfirmationModal
        visible={showApproveConfirm}
        onClose={handleCloseConfirm}
        onConfirm={() => onProcessAction("Approved")}
        title={confirmationConfig.title}
        message={`Are you sure you want to ${confirmationConfig.actionText} this ${targetRequest?.requestTypeDescription} for ${targetRequest?.fullName}?`}
        type="info"
      />

      {/* Disapproval Modal (Uniform Design) */}
      <DisapproveReasonModal
        isOpen={showDisapproveModal}
        onClose={() => setShowDisapproveModal(false)}
        onSave={(remarks) => onProcessAction("Disapproved", remarks)}
      />

      <GeneralMessage form={showMessage} message={message} isSuccess={isSuccess} />
      <LoadingSpinner loading={loading} />
    </div>
  );
}